import { h, JSX } from '@stencil/core';
import { isShopAvailable } from '../../utility/helpers/shop-helper';
import animationManager from '../../services/animations/animation-manager';
import { getRightTheme } from '../../utility/helper';
import { navContentStore } from '../../state/nav-content-store';
import { pureButtonSkeleton } from '../../utility/skeleton';
import { ContextualIconProps, ContextualIconResponse } from '../../utility/constants/app';
import {
  EventActions,
  NavigationMode,
  NavigationModeType,
  NavigationTheme,
  NavigationThemeType
} from '../../utility/constants';
import { shopStateStore } from '../../state/shop-state-store';
import globalDataLayerService from '../../services/analytics/global-data-layer-service';

export default function shopIcons({
  locale,
  app,
  mode,
  theme,
  isNavigationLoaded
}: ContextualIconProps): ContextualIconResponse {
  function getShoppingCartLink(): string {
    const shopLink = navContentStore.state.shop?.cart;

    if (shopLink !== undefined && shopLink !== '') {
      return `${shopLink}`;
    }
    return '';
  }

  function showShoppingCart(): string {
    const { numberOfShoppingItems, shopInformation } = shopStateStore.state;
    let numberOfItems = shopInformation.numberOfShoppingItems;
    if (numberOfItems === 0) {
      numberOfItems = numberOfShoppingItems;
    }

    switch (true) {
      case numberOfItems === 0:
        return 'shopping-bag';
      case numberOfItems > 0:
        return 'shopping-bag-filled';
      default:
        return 'shopping-bag';
    }
  }

  function getAriaLabelForShoppingCart(): string {
    const { shopInformation } = shopStateStore.state;

    return `My shopping bag, ${shopInformation.numberOfShoppingItems} items, estimated subtotal ${shopInformation.subTotal}`;
  }

  return {
    leftIcons: isShopAvailable(locale, app) ? (
      <div class="icon">
        <phn-p-button-pure
          icon="search"
          hide-label="true"
          stretch="true"
          aria="{ 'aria-haspopup': 'dialog', 'aria-label' : 'open the shop search field' }"
          data-test-id="search-button"
          onClick={() => {
            animationManager.play('openContextualDrawer');
            sendDataLayerEventContextualDrawer();
          }}
          style={isNavigationLoaded ? {} : { display: 'none' }}
          theme={getRightTheme(theme, mode)}
        />
        {!isNavigationLoaded ? pureButtonSkeleton() : null}
      </div>
    ) : null,
    rightIcons: isShopAvailable(locale, app) ? (
      <div class="icon">
        <phn-p-link-pure
          target={'_self'}
          hide-label="true"
          icon="none"
          theme={getRightTheme(theme, mode)}
          style={isNavigationLoaded ? {} : { display: 'none' }}
          class="shopping-cart-link"
          data-test-id="shopping-cart-link"
          onClick={() => {
            sendDataLayerEventShoppingCart(getShoppingCartLink());
          }}
        >
          <a href={getShoppingCartLink()} aria-label={getAriaLabelForShoppingCart()}>
            <div class="icon-flex">
              <ShoppingCartCount styleClass="shopping-item-count" theme={theme} mode={mode} />
              <phn-p-icon
                theme={getRightTheme(theme, mode)}
                name={showShoppingCart()}
                data-test-id="shopping-cart-link-icon"
              />
            </div>
          </a>
        </phn-p-link-pure>
        {!isNavigationLoaded ? pureButtonSkeleton() : null}
      </div>
    ) : null
  };
}

const ShoppingCartCount = ({
  styleClass,
  theme,
  mode
}: {
  styleClass: string;
  theme: NavigationThemeType;
  mode: NavigationModeType;
}): JSX.Element => {
  const shoppingItemCountOver99 = '+99';
  const { numberOfShoppingItems, shopInformation } = shopStateStore.state;
  let numberOfItems = shopInformation.numberOfShoppingItems;
  if (numberOfItems === 0) {
    numberOfItems = numberOfShoppingItems;
  }
  const displayedCount = numberOfItems < 99 ? numberOfItems : shoppingItemCountOver99;

  if (numberOfItems <= 0) return '';

  return (
    <span
      class={styleClass}
      style={mode === NavigationMode.hero && theme === NavigationTheme.dark ? { color: 'black' } : { color: 'white' }}
    >
      {displayedCount}
    </span>
  );
};

function sendDataLayerEventContextualDrawer() {
  globalDataLayerService.pushDataLayerEvent(EventActions.CONTEXTUAL_BUTTON_CLICK, {
    componentClick: {
      clickElementId: 'mainmenu.shop.search',
      clickElementName: 'Shop search'
    }
  });
}

function sendDataLayerEventShoppingCart(url: string) {
  globalDataLayerService.pushDataLayerEvent(EventActions.CONTEXTUAL_BUTTON_CLICK, {
    componentClick: {
      clickElementId: 'mainmenu.shop.shoppingbag',
      clickElementName: 'Shopping cart',
      targetUrl: url
    }
  });
}
